import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_ALL_USERS, BLOCK_USER_MUTATION, UNBLOCK_USER_MUTATION } from '../../apollo/queries';
import { useAuth } from '../AuthContext';
import '../css/BlockedCustomerPage.css';

const BlockedCustomerPage = () => {
    const navigate = useNavigate();
    const { user, isAdmin } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [searchType, setSearchType] = useState('email');
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [selectedLevels, setSelectedLevels] = useState(['all']);
    const [selectedUsers, setSelectedUsers] = useState([]);

    // GraphQL 쿼리 및 뮤테이션
    const { data, loading, error, refetch } = useQuery(GET_ALL_USERS, {
        errorPolicy: 'all'
    });
    
    const [blockUser] = useMutation(BLOCK_USER_MUTATION, {
        onCompleted: () => {
            refetch();
            alert('회원이 차단되었습니다.');
        },
        onError: (error) => {
            console.error('차단 처리 중 오류:', error);
            alert('차단 처리 중 오류가 발생했습니다.');
        }
    });
    
    const [unblockUser] = useMutation(UNBLOCK_USER_MUTATION, {
        onCompleted: () => {
            refetch();
            alert('차단이 해제되었습니다.');
        },
        onError: (error) => {
            console.error('차단 해제 중 오류:', error);
            alert('차단 해제 중 오류가 발생했습니다.');
        }
    });

    // 로딩 중일 때
    if (loading) {
        return (
            <div className="admin-main-content">
                <main className="admin-content">
                    <h2>차단 회원 관리</h2>
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <div>회원 정보를 불러오는 중입니다...</div>
                    </div>
                </main>
            </div>
        );
    }

    // 에러 발생 시
    if (error) {
        return (
            <div className="admin-main-content">
                <main className="admin-content">
                    <h2>차단 회원 관리</h2>
                    <div style={{ textAlign: 'center', padding: '50px', color: '#ff4444' }}>
                        <div>데이터를 불러오는 중 오류가 발생했습니다: {error.message}</div>
                    </div>
                </main>
            </div>
        );
    }

    const users = data?.users || [];
    const activeUsers = users.filter(user => user.isActive);
    const blockedUsers = users.filter(user => !user.isActive);

    // 검색 및 필터링
    const filteredUsers = activeUsers.filter(user => {
        // 검색어 필터링
        if (searchTerm) {
            let searchField = '';
            switch (searchType) {
                case 'email':
                    searchField = user.email || '';
                    break;
                case 'name':
                    searchField = user.name || '';
                    break;
                case 'company':
                    searchField = user.company || '';
                    break;
                default:
                    searchField = user.email || '';
            }
            if (!searchField.toLowerCase().includes(searchTerm.toLowerCase())) {
                return false;
            }
        }

        // 날짜 범위 필터링
        if (dateRange.start || dateRange.end) {
            const userDate = new Date(user.createdAt);
            const startDate = dateRange.start ? new Date(dateRange.start) : new Date('1900-01-01');
            const endDate = dateRange.end ? new Date(dateRange.end) : new Date('2100-12-31');
            
            if (userDate < startDate || userDate > endDate) {
                return false;
            }
        }

        // 등급 필터링
        if (!selectedLevels.includes('all')) {
            if (!selectedLevels.includes(user.level)) {
                return false;
            }
        }

        return true;
    });

    // 차단된 회원 필터링
    const filteredBlockedUsers = blockedUsers.filter(user => {
        if (searchTerm) {
            let searchField = '';
            switch (searchType) {
                case 'email':
                    searchField = user.email || '';
                    break;
                case 'name':
                    searchField = user.name || '';
                    break;
                case 'company':
                    searchField = user.company || '';
                    break;
                default:
                    searchField = user.email || '';
            }
            if (!searchField.toLowerCase().includes(searchTerm.toLowerCase())) {
                return false;
            }
        }
        return true;
    });

    const handleBlockUser = async (userId) => {
        if (window.confirm('정말로 이 회원을 차단하시겠습니까?')) {
            try {
                await blockUser({
                    variables: { userId: parseInt(userId) }
                });
            } catch (error) {
                console.error('차단 처리 실패:', error);
            }
        }
    };

    const handleUnblockUser = async (userId) => {
        if (window.confirm('정말로 이 회원의 차단을 해제하시겠습니까?')) {
            try {
                await unblockUser({
                    variables: { userId: parseInt(userId) }
                });
            } catch (error) {
                console.error('차단 해제 실패:', error);
            }
        }
    };

    const handleUserSelect = (userId) => {
        setSelectedUsers(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };

    const handleBulkBlock = async () => {
        if (selectedUsers.length === 0) {
            alert('차단할 회원을 선택해주세요.');
            return;
        }

        if (window.confirm(`선택된 ${selectedUsers.length}명의 회원을 차단하시겠습니까?`)) {
            try {
                for (const userId of selectedUsers) {
                    await blockUser({
                        variables: { userId: parseInt(userId) }
                    });
                }
                setSelectedUsers([]);
            } catch (error) {
                console.error('일괄 차단 처리 실패:', error);
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return `${date.getFullYear().toString().slice(-2)}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    };

    return (
        <>
            <div className="breadcrumb">
                <span>홈 &gt; 회원 정보 관리 &gt; 차단 회원 관리</span>
            </div>
            <div className="admin-main-content">
                <aside className="admin-sidebar">
                    <div className="sidebar-menu">
                        <h3>회원 정보 관리</h3>
                        <ul>
                            <li onClick={() => navigate('/admin/customer')}>회원 정보 관리</li>
                            <li onClick={() => navigate('/admin/customer/register')}>회원 등록하기</li>
                            <li className="active">차단 회원 관리</li>
                        </ul>
                    </div>
                    <div className="sidebar-menu">
                        <h3>가입통계</h3>
                        <ul>
                            <li onClick={() => navigate('/admin/stats/signup')}>월별 가입 통계</li>
                        </ul>
                    </div>
                </aside>

                <main className="admin-content">
                    <div className="page-header">
                        <div className="page-title-bar"></div>
                        <h2>차단 회원 관리</h2>
                    </div>

                    {/* 검색 필터 */}
                    <div className="customer-search-filters">
                        <div className="filter-row">
                            <label>검색명</label>
                            <select 
                                value={searchType}
                                onChange={(e) => setSearchType(e.target.value)}
                                className="filter-select"
                            >
                                <option value="email">아이디</option>
                                <option value="name">이름</option>
                                <option value="company">회사명</option>
                            </select>
                            <input 
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="검색어를 입력하세요"
                                className="filter-input"
                            />
                        </div>
                        <div className="filter-row">
                            <label>가입일</label>
                            <input 
                                type="date"
                                value={dateRange.start}
                                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                                className="date-input"
                            />
                            <span>~</span>
                            <input 
                                type="date"
                                value={dateRange.end}
                                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                                className="date-input"
                            />
                        </div>
                    </div>

                    {/* 활성 회원 목록 */}
                    <div className="customer-section">
                        <div className="section-header">
                            <h3>활성 회원 목록 ({filteredUsers.length}명)</h3>
                            <button 
                                onClick={handleBulkBlock}
                                className="btn-block-selected"
                                disabled={selectedUsers.length === 0}
                            >
                                선택 회원 차단 ({selectedUsers.length})
                            </button>
                        </div>

                        <div className="customer-table-container">
                            <table className="customer-table">
                                <thead>
                                    <tr>
                                        <th>선택</th>
                                        <th>아이디</th>
                                        <th>회사명</th>
                                        <th>담당자</th>
                                        <th>연락처</th>
                                        <th>등급</th>
                                        <th>가입일</th>
                                        <th>액션</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map((user) => (
                                        <tr key={user.id}>
                                            <td>
                                                <input 
                                                    type="checkbox"
                                                    checked={selectedUsers.includes(user.id)}
                                                    onChange={() => handleUserSelect(user.id)}
                                                />
                                            </td>
                                            <td data-label="아이디">{user.email}</td>
                                            <td data-label="회사명">{user.company || '-'}</td>
                                            <td data-label="담당자">{user.name}</td>
                                            <td data-label="연락처">{user.mobile || user.phone || '-'}</td>
                                            <td data-label="등급">{user.level || '-'}</td>
                                            <td data-label="가입일">{formatDate(user.createdAt)}</td>
                                            <td data-label="액션">
                                                <button 
                                                    className="btn-block"
                                                    onClick={() => handleBlockUser(user.id)}
                                                >
                                                    차단
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {filteredUsers.length === 0 && (
                                <div className="empty-state">
                                    활성 회원이 없습니다.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 차단된 회원 목록 */}
                    <div className="customer-section">
                        <div className="section-header">
                            <h3>차단된 회원 목록 ({filteredBlockedUsers.length}명)</h3>
                        </div>

                        <div className="customer-table-container">
                            <table className="customer-table">
                                <thead>
                                    <tr>
                                        <th>아이디</th>
                                        <th>회사명</th>
                                        <th>담당자</th>
                                        <th>연락처</th>
                                        <th>등급</th>
                                        <th>가입일</th>
                                        <th>차단일</th>
                                        <th>액션</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredBlockedUsers.map((user) => (
                                        <tr key={user.id}>
                                            <td data-label="아이디">{user.email}</td>
                                            <td data-label="회사명">{user.company || '-'}</td>
                                            <td data-label="담당자">{user.name}</td>
                                            <td data-label="연락처">{user.mobile || user.phone || '-'}</td>
                                            <td data-label="등급">{user.level || '-'}</td>
                                            <td data-label="가입일">{formatDate(user.createdAt)}</td>
                                            <td data-label="차단일">{formatDate(user.blockedAt)}</td>
                                            <td data-label="액션">
                                                <button 
                                                    className="btn-unblock"
                                                    onClick={() => handleUnblockUser(user.id)}
                                                >
                                                    차단 해제
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {filteredBlockedUsers.length === 0 && (
                                <div className="empty-state">
                                    차단된 회원이 없습니다.
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="page-actions">
                        <button className="btn-secondary" onClick={() => navigate(-1)}>
                            &lt; 돌아가기
                        </button>
                    </div>
                </main>
            </div>
        </>
    );
};

export default BlockedCustomerPage; 